/**
 * Pure data pipeline: applies filter and sort rules to raw data.
 * Never mutates the input array.
 */

const STRING_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const NUMBER_OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'between', label: 'between' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

export function getOperatorsForType(type) {
  return type === 'number' ? NUMBER_OPERATORS : STRING_OPERATORS;
}

function isEmpty(val) {
  return val === null || val === undefined || String(val).trim() === '';
}

function evaluateRule(row, rule, columns) {
  const cellValue = row[rule.column];
  const col = columns.find(c => c.name === rule.column);
  const isNumeric = col?.type === 'number';

  if (rule.operator === 'is_empty') return isEmpty(cellValue);
  if (rule.operator === 'is_not_empty') return !isEmpty(cellValue);

  if (isNumeric) {
    const num = Number(cellValue);
    const target = Number(rule.value);
    if (isNaN(num)) return false;

    switch (rule.operator) {
      case 'eq': return num === target;
      case 'neq': return num !== target;
      case 'gt': return num > target;
      case 'gte': return num >= target;
      case 'lt': return num < target;
      case 'lte': return num <= target;
      case 'between': {
        const parts = String(rule.value).split(',').map(s => Number(s.trim()));
        if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return true;
        const lo = Math.min(parts[0], parts[1]);
        const hi = Math.max(parts[0], parts[1]);
        return num >= lo && num <= hi;
      }
      default: return true;
    }
  }

  // String comparisons (case-insensitive)
  const str = String(cellValue ?? '').toLowerCase();
  const target = String(rule.value ?? '').toLowerCase();

  switch (rule.operator) {
    case 'equals': return str === target;
    case 'not_equals': return str !== target;
    case 'contains': return str.includes(target);
    case 'not_contains': return !str.includes(target);
    case 'starts_with': return str.startsWith(target);
    case 'ends_with': return str.endsWith(target);
    default: return true;
  }
}

function compareValues(a, b, direction, colType) {
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let result;
  if (colType === 'number') {
    result = Number(a) - Number(b);
  } else {
    result = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  }
  return direction === 'desc' ? -result : result;
}

export function applyDataPipeline(rawData, columns, filterRules, sortRules) {
  if (!rawData || rawData.length === 0) return rawData;
  if ((!filterRules || filterRules.length === 0) && (!sortRules || sortRules.length === 0)) {
    return rawData;
  }

  let data = rawData;

  const activeFilters = (filterRules || []).filter(r => r.enabled);
  if (activeFilters.length > 0) {
    data = data.filter(row => activeFilters.every(rule => evaluateRule(row, rule, columns)));
  }

  if (sortRules && sortRules.length > 0) {
    data = [...data].sort((a, b) => {
      for (const rule of sortRules) {
        const col = columns.find(c => c.name === rule.column);
        const result = compareValues(a[rule.column], b[rule.column], rule.direction, col?.type);
        if (result !== 0) return result;
      }
      return 0;
    });
  }

  return data;
}
